import { supabase } from '../lib/supabase';
import { createLogger } from '../lib/logger';
import { NetworkError, InsufficientCreditsError, DatabaseError } from '../lib/errors';
import { invokeEdgeFunction, isEdgeFunctionError } from '../lib/edgeFunctions';

const logger = createLogger('CreditService');

export function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

export interface CreditBalance {
  balance: number;
  currency: string;
}

export interface CreditTransaction {
  id: string;
  organisation_id: string;
  transaction_type: 'debit' | 'credit' | 'topup' | 'refund';
  amount: number;
  balance_after: number;
  action_type: string | null;
  description: string;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
}

export interface PriceBookEntry {
  id: string;
  action_type: string;
  name: string;
  description: string | null;
  cost: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function getBalance(): Promise<CreditBalance> {
  try {
    return await invokeEdgeFunction<CreditBalance>('credit-operations', {
      action: 'get_balance',
    });
  } catch (err) {
    if (isEdgeFunctionError(err)) {
      throw new NetworkError(err.message || 'fetching balance', err.status);
    }
    throw err;
  }
}

export async function deductCredits(
  actionType: string,
  metadata?: Record<string, unknown>,
  idempotencyKey?: string
): Promise<{ success: boolean; balance: number; cost: number; transaction_id?: string }> {
  logger.debug('Starting credit deduction:', { actionType, metadata });

  const requestBody = {
    action: 'deduct',
    actionType,
    metadata,
    idempotencyKey,
  };

  logger.debug('Request:', requestBody);

  try {
    const result = await invokeEdgeFunction<{
      success: boolean;
      balance: number;
      cost: number;
      transaction_id?: string;
    }>('credit-operations', requestBody);

    logger.debug('Deduction successful:', result);
    return result;
  } catch (err) {
    if (isEdgeFunctionError(err)) {
      logger.error('Error response:', { status: err.status, body: err });

      if (err.status === 402) {
        throw new InsufficientCreditsError(
          (err.required as number) || 0,
          (err.available as number) || 0,
        );
      }

      throw new NetworkError(
        err.details
          ? `${err.message} (${err.details})`
          : err.message || 'deducting credits',
        err.status,
      );
    }
    throw err;
  }
}

/**
 * Open the TokenForge billing portal — the only way tokens enter the ledger
 * since the self-serve topup action was retired (it let any org member grant
 * themselves credits with no payment behind it). The portal URL is minted
 * server-side with a five-minute token; this client never sees an API key.
 */
export async function openBillingPortal(): Promise<string> {
  const result = await invokeEdgeFunction<{ portal_url: string }>('tokenforge-portal', {
    return_url: window.location.href,
  });
  return result.portal_url;
}

export interface TransactionFilters {
  dateFrom?: string;       // ISO date string YYYY-MM-DD
  dateTo?: string;         // ISO date string YYYY-MM-DD
  transactionType?: string; // 'debit' | 'credit' | 'topup' | 'refund'
  actionType?: string;      // e.g. 'document_generation', 'ai_adviser_query'
  limit?: number;           // default unlimited when filters applied, 100 otherwise
}

export async function getTransactions(
  organisationId: string,
  filters?: TransactionFilters,
): Promise<CreditTransaction[]> {
  logger.debug('Fetching transactions for org:', organisationId, 'filters:', filters);

  let query = supabase
    .from('credit_transactions')
    .select('*')
    .eq('organisation_id', organisationId)
    .order('created_at', { ascending: false });

  if (filters?.dateFrom) {
    query = query.gte('created_at', `${filters.dateFrom}T00:00:00`);
  }
  if (filters?.dateTo) {
    query = query.lte('created_at', `${filters.dateTo}T23:59:59`);
  }
  if (filters?.transactionType) {
    query = query.eq('transaction_type', filters.transactionType);
  }
  if (filters?.actionType) {
    query = query.eq('action_type', filters.actionType);
  }

  // When filters are applied, fetch up to 500; default view also shows up to 500
  const rowLimit = filters?.limit ?? 500;
  query = query.limit(rowLimit);

  const { data, error } = await query;

  if (error) {
    throw new DatabaseError('fetching transactions', error);
  }

  logger.debug(`Found ${data?.length || 0} transactions`);
  return data || [];
}

export async function getPriceBook(): Promise<PriceBookEntry[]> {
  const { data, error } = await supabase
    .from('price_book')
    .select('*')
    .eq('is_active', true)
    .order('cost', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function refundCredits(
  transactionId: string,
  reason: string,
  idempotencyKey?: string
): Promise<{ success: boolean; balance: number }> {
  try {
    return await invokeEdgeFunction<{ success: boolean; balance: number }>(
      'credit-operations',
      {
        action: 'refund',
        transactionId,
        reason,
        idempotencyKey,
      },
    );
  } catch (err) {
    if (isEdgeFunctionError(err)) {
      throw new NetworkError(err.message || 'refunding credits', err.status);
    }
    throw err;
  }
}

export async function checkCreditsAvailable(
  actionType: string
): Promise<{ available: boolean; cost: number; balance: number }> {
  logger.debug('Checking credit availability:', { actionType });

  const requestBody = {
    action: 'check_available',
    actionType,
  };

  logger.debug('Check request:', requestBody);

  try {
    const result = await invokeEdgeFunction<{
      available: boolean;
      cost: number;
      balance: number;
    }>('credit-operations', requestBody);

    logger.debug('Availability check result:', result);
    return result;
  } catch (err) {
    if (isEdgeFunctionError(err)) {
      logger.error('Check error:', { status: err.status, body: err });
      throw new NetworkError(
        err.details
          ? `${err.message} (${err.details})`
          : err.message || 'checking credits',
        err.status,
      );
    }
    throw err;
  }
}
